import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MdAdd, MdCheckCircle, MdDownload, MdError, MdHourglassEmpty, MdInfo,
  MdList, MdNfc, MdOutlineScale, MdPlayArrow, MdStopCircle, MdTune, MdUndo, MdUsb,
} from 'react-icons/md';
import { TbScale } from 'react-icons/tb';
import AjudaTela from '../../components/AjudaTela';
import Layout from '../../components/Layout';
import { api, listarTudo } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const nf = (n) =>
  Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function normalizar(bruto) {
  const d = String(bruto).replace(/\D/g, '');
  return d.length >= 15 ? d.slice(-15) : d;
}

// Feedback sonoro — conforto, não requisito
let audioCtx = null;
function bip(tipo) {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const seq = { ok: [880, 1320], erro: [300, 220], aviso: [660, 660], undo: [520, 380] }[tipo] || [600];
    seq.forEach((hz, i) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.type = 'square';
      o.frequency.value = hz;
      const t0 = audioCtx.currentTime + i * 0.09;
      g.gain.setValueAtTime(0.05, t0);
      g.gain.exponentialRampToValueAtTime(0.0008, t0 + 0.085);
      o.connect(g);
      g.connect(audioCtx.destination);
      o.start(t0);
      o.stop(t0 + 0.09);
    });
  } catch (_) { /* som é conforto */ }
}

// ─── Paleta ───────────────────────────────────────────────────────────────
// Tela pensada pra uso no curral: alto contraste, fonte de sistema (sem CDN
// — precisa abrir offline), pouca cor mas com propósito. Deliberadamente
// diferente do resto do sistema (fundo claro), porque o contexto é outro:
// tela de celular/notebook no sol, com luva, de longe.

const P = {
  bg: '#0A130F',
  painel: '#132A1D',
  painelAlt: '#0D1E15',
  borda: '#22402C',
  bordaSuave: 'rgba(34,64,44,.6)',
  texto: '#F3F1E6',
  textoMuted: '#93A497',
  destaque: '#DDA84A',       // âmbar — rótulos, valores em atenção
  destaqueSuave: 'rgba(221,168,74,.14)',
  sucesso: '#5FCB74',
  sucessoSuave: 'rgba(95,203,116,.14)',
  perigo: '#E3644B',
  perigoSuave: 'rgba(227,100,75,.14)',
  info: '#4C86FF',
  infoSuave: 'rgba(76,134,255,.14)',
  neutro: '#4E6256',
};

const AJUDA = {
  titulo: 'Pesagem no Brete',
  descricao:
    'Tela pra pesar o lote inteiro no curral, lendo o brinco eletrônico e o peso da balança em qualquer ordem. Funciona sem internet: cada leitura é salva na hora e sobe pro servidor sozinha quando a conexão voltar.',
  passos: [
    { titulo: '1. Inicie a sessão', texto: 'A sessão abre sozinha ao entrar na tela (se houver conexão). Cada sessão agrupa os animais pesados numa mesma passada pelo brete.' },
    { titulo: '2. Leia o brinco eletrônico', texto: 'Passe o bastão leitor no brinco do animal — não precisa clicar em nenhum campo antes, a leitura funciona com a página em foco. Também dá pra digitar os 15 dígitos e apertar Enter.' },
    { titulo: '3. Pese o animal', texto: 'Com a balança USB conectada ("Conectar balança"), o peso aparece sozinho. Sem balança, digite o peso no campo indicado.' },
    { titulo: '4. A ordem não importa', texto: 'Pode ler o brinco antes ou depois de pesar. Assim que as duas informações estiverem prontas e o peso estável, a linha é registrada sozinha — sem precisar clicar em nada — e soa um bipe de confirmação.' },
    { titulo: '5. Errou? Desfaça', texto: 'Aperte F2 (ou clique em "Desfazer último") pra remover o último registro da sessão.' },
    { titulo: '6. Fechar a sessão', texto: 'Ao terminar o lote, clique em "Fechar sessão" — os registros viram pesagens de verdade no histórico de cada animal.' },
  ],
  dicas: [
    'Animal ainda não cadastrado? A leitura entra marcada como "novo" (pendência), e pode ser associada a um cadastro depois.',
    'Os botões "Simular balança" e "Simular brinco" servem só pra testar a tela sem nenhum equipamento conectado.',
    'Em "Ajustes da sessão" dá pra calibrar a tolerância de estabilidade, quantas leituras estáveis são exigidas, e em quantos segundos um brinco sem peso expira — ajuste conforme o comportamento da balança de vocês.',
    'O indicador "Online/Offline" no topo mostra o estado da conexão; "Buffer" aparece quando há leituras aguardando para subir ao servidor.',
  ],
};

// ─── Subcomponentes ──────────────────────────────────────────────────────────

function Chip({ on, off, alerta, label }) {
  const cor = alerta ? P.destaque : on ? P.sucesso : off ? P.perigo : P.neutro;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '7px', fontSize: '.72rem', fontWeight: 700,
      padding: '6px 12px', border: `1px solid ${P.borda}`, borderRadius: '999px',
      background: P.painelAlt, textTransform: 'uppercase', letterSpacing: '.05em',
      color: (on || alerta) ? P.texto : P.textoMuted,
    }}>
      <span style={{
        width: '7px', height: '7px', borderRadius: '50%', background: cor, flexShrink: 0,
        boxShadow: (on || alerta) ? `0 0 8px ${cor}` : 'none',
      }} />
      {label}
    </div>
  );
}

function Painel({ titulo, icone, children, style }) {
  return (
    <div style={{
      background: P.painel, border: `1px solid ${P.borda}`, borderRadius: '14px',
      boxShadow: '0 1px 2px rgba(0,0,0,.25), 0 14px 28px -20px rgba(0,0,0,.75)',
      overflow: 'hidden', ...style,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '9px', padding: '13px 18px',
        borderBottom: `1px solid ${P.borda}`,
        background: 'linear-gradient(180deg, rgba(255,255,255,.03), transparent)',
      }}>
        {icone && <span style={{ color: P.destaque, display: 'flex', fontSize: '1rem' }}>{icone}</span>}
        <h2 style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.12em', color: P.destaque, fontWeight: 700, margin: 0 }}>{titulo}</h2>
      </div>
      {children}
    </div>
  );
}

function Contador({ valor, label, aviso }) {
  return (
    <div style={{ background: P.painel, padding: '14px 13px' }}>
      <b style={{ display: 'block', fontFamily: 'ui-monospace,monospace', fontSize: '1.8rem', fontWeight: 700, lineHeight: 1, color: aviso ? P.destaque : P.texto, fontVariantNumeric: 'tabular-nums' }}>{valor}</b>
      <span style={{ fontSize: '.66rem', textTransform: 'uppercase', letterSpacing: '.08em', color: P.textoMuted }}>{label}</span>
    </div>
  );
}

function Ajuste({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', fontSize: '.85rem', color: P.texto }}>
      <span>{label}</span>
      <input type="text" className="bp-input" value={value} onChange={e => onChange(e.target.value)}
        style={{ width: '82px', padding: '8px 10px', fontFamily: 'ui-monospace,monospace', fontSize: '.9rem', background: P.painelAlt, color: P.texto, border: `1px solid ${P.borda}`, borderRadius: '6px', textAlign: 'right' }} />
    </div>
  );
}

const btnBase = {
  display: 'inline-flex', alignItems: 'center', gap: '7px',
  fontFamily: 'system-ui', fontSize: '.85rem', fontWeight: 600,
  padding: '10px 16px', borderRadius: '8px', border: `1px solid ${P.borda}`,
  background: '#1A3D2A', color: P.texto,
};
function Btn({ onClick, children, disabled, icone }) {
  return (
    <button className="bp-btn" onClick={onClick} disabled={disabled}
      style={{ ...btnBase, opacity: disabled ? .4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {icone}{children}
    </button>
  );
}
function BtnPrimario({ onClick, children, disabled, icone }) {
  return (
    <button className="bp-btn" onClick={onClick} disabled={disabled}
      style={{ ...btnBase, background: P.info, borderColor: '#6E9CFF', opacity: disabled ? .4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {icone}{children}
    </button>
  );
}
function BtnPerigo({ onClick, children, disabled, icone }) {
  return (
    <button className="bp-btn" onClick={onClick} disabled={disabled}
      style={{ ...btnBase, background: 'transparent', borderColor: P.perigo, color: '#F3B4A4', opacity: disabled ? .4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {icone}{children}
    </button>
  );
}

// Estilos que inline style não cobre (hover, foco, redução de movimento).
// Só aparência — nenhum destes seletores participa de lógica.
function EstilosBrete() {
  return (
    <style>{`
      .bp-btn { transition: filter .15s ease, transform .08s ease; }
      .bp-btn:hover:not(:disabled) { filter: brightness(1.14); }
      .bp-btn:active:not(:disabled) { transform: translateY(1px); }
      .bp-btn:focus-visible { outline: 2px solid ${P.info}; outline-offset: 2px; }
      .bp-input { transition: border-color .15s ease, box-shadow .15s ease; }
      .bp-input:focus { border-color: ${P.info} !important; box-shadow: 0 0 0 3px ${P.infoSuave}; outline: none; }
      .bp-row:hover td { background: rgba(255,255,255,.035); }
      .bp-dot-viva { animation: bp-pulso 1.6s ease-in-out infinite; }
      @keyframes bp-pulso { 0%,100% { opacity: 1; } 50% { opacity: .55; } }
      @media (prefers-reduced-motion: reduce) {
        .bp-btn, .bp-input { transition: none; }
        .bp-dot-viva { animation: none; }
      }
    `}</style>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function BreteAjuste() {
  const { podeGravar } = useAuth();

  const [sessao, setSessao] = useState(null);
  const [erroSessao, setErroSessao] = useState(null);
  const [linhas, setLinhas] = useState([]);
  const [peso, setPeso] = useState(null);
  const [pesoEstavel, setPesoEstavel] = useState(false);
  const [tagInputVal, setTagInputVal] = useState('');
  const [tagAtiva, setTagAtiva] = useState(null);     // { cod, nova }
  const [serial, setSerial] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [fechando, setFechando] = useState(false);
  const [feedback, setFeedback] = useState({ tipo: 'aguardando', msg: 'Aguardando animal', sub: 'Passe o bastão no brinco e ponha o animal na balança — a ordem não importa.' });
  const [ajusteTol, setAjusteTol] = useState('2,0');
  const [ajusteJan, setAjusteJan] = useState('5');
  const [ajusteExp, setAjusteExp] = useState('25');

  // Refs: estado mutable para closures sem stale reads
  const sessaoRef = useRef(null);
  const tagAtivaRef = useRef(null);       // espelha tagAtiva
  const tagEmRef = useRef(0);
  const pesoRef = useRef(null);           // espelha peso
  const pesoEstavelRef = useRef(false);   // espelha pesoEstavel
  const amostrasRef = useRef([]);
  const animaisMapRef = useRef(new Map()); // codigo_eletronico → animal.id
  const bufferOfflineRef = useRef([]);    // linhas aguardando envio
  const registrandoRef = useRef(false);   // previne duplo-disparo
  const bufHIDRef = useRef('');
  const ultimaTeclaRef = useRef(0);
  const tRajadaRef = useRef(null);
  const simulandoRef = useRef(null);

  // Sincroniza refs com estado
  useEffect(() => { sessaoRef.current = sessao; }, [sessao]);
  useEffect(() => { tagAtivaRef.current = tagAtiva; }, [tagAtiva]);
  useEffect(() => { pesoRef.current = peso; pesoEstavelRef.current = pesoEstavel; }, [peso, pesoEstavel]);

  // Carrega mapa codigo_eletronico → animal.id
  useEffect(() => {
    listarTudo('/pecuaria/animal/').then(animais => {
      const m = new Map();
      animais.forEach(a => { if (a.codigo_eletronico) m.set(a.codigo_eletronico, a.id); });
      animaisMapRef.current = m;
    }).catch(() => {});
  }, []);

  // Cria sessão ao montar
  useEffect(() => {
    if (!podeGravar('pecuaria.sessao_pesagem')) return;
    api.post('/pecuaria/sessao-pesagem/', { tipo_evento: 'pesagem', aberta_em: new Date().toISOString() })
      .then(r => { setSessao(r.data); sessaoRef.current = r.data; })
      .catch(() => setErroSessao('Não foi possível criar a sessão. Verifique a conexão.'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Online / offline
  useEffect(() => {
    const handleOnline = () => { setOnline(true); flushBuffer(); };
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Captura HID (bastão como teclado: dígitos em < 60 ms + Enter)
  useEffect(() => {
    const handler = (ev) => {
      if (ev.key === 'F2') { ev.preventDefault(); desfazer(); return; }
      const agora = performance.now();
      const rapido = agora - ultimaTeclaRef.current < 60;
      ultimaTeclaRef.current = agora;
      if (/^\d$/.test(ev.key)) {
        if (!rapido) bufHIDRef.current = '';
        bufHIDRef.current += ev.key;
        clearTimeout(tRajadaRef.current);
        tRajadaRef.current = setTimeout(() => { bufHIDRef.current = ''; }, 700);
        return;
      }
      if (ev.key === 'Enter') {
        const cod = normalizar(bufHIDRef.current);
        bufHIDRef.current = '';
        if (cod.length === 15) { ev.preventDefault(); receberTag(cod); }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Expiração do brinco sem peso
  useEffect(() => {
    const t = setInterval(() => {
      if (!tagAtivaRef.current || pesoRef.current != null) return;
      const limite = (parseInt(ajusteExp) || 25) * 1000;
      if (Date.now() - tagEmRef.current > limite) {
        tagAtivaRef.current = null;
        setTagAtiva(null);
        setTagInputVal('');
        bip('erro');
        setFeedback({ tipo: 'erro', msg: 'Brinco descartado por tempo', sub: 'O animal saiu sem pesar. Passe o bastão de novo.' });
        setTimeout(() => setFeedback({ tipo: 'aguardando', msg: 'Aguardando animal', sub: 'Passe o bastão e ponha o animal na balança.' }), 2000);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [ajusteExp]);

  // ── Funções de entrada ────────────────────────────────────────────────────

  function avaliar(cod, nova, kg, estavel) {
    if (cod && kg != null && estavel) {
      registrar(cod, nova, kg);
      return;
    }
    if (cod && kg == null) {
      setFeedback({ tipo: 'parcial', msg: 'Brinco lido — falta o peso', sub: cod + (nova ? ' · animal não cadastrado' : '') });
    } else if (cod && !estavel) {
      setFeedback({ tipo: 'parcial', msg: 'Aguardando peso estabilizar', sub: 'O animal ainda está se mexendo.' });
    } else if (kg != null) {
      setFeedback({ tipo: 'parcial', msg: 'Peso na balança — falta o brinco', sub: 'Passe o bastão no brinco do animal.' });
    } else {
      setFeedback({ tipo: 'aguardando', msg: 'Aguardando animal', sub: 'Passe o bastão e ponha o animal na balança — a ordem não importa.' });
    }
  }

  function receberTag(cod) {
    if (linhas.some(l => l.tag === cod)) {
      bip('erro');
      setFeedback({ tipo: 'erro', msg: 'Este brinco já foi lido nesta sessão', sub: 'Confira se não é o animal vizinho.' });
      return;
    }
    const nova = !animaisMapRef.current.has(cod);
    tagAtivaRef.current = { cod, nova };
    tagEmRef.current = Date.now();
    setTagAtiva({ cod, nova });
    setTagInputVal(cod);
    bip(nova ? 'aviso' : 'ok');
    avaliar(cod, nova, pesoRef.current, pesoEstavelRef.current);
  }

  function receberPeso(kg, doHardware) {
    amostrasRef.current.push(kg);
    const janela = Math.max(2, parseInt(ajusteJan) || 5);
    const tol = parseFloat(ajusteTol.replace(',', '.')) || 2;
    if (amostrasRef.current.length > janela) amostrasRef.current.shift();
    const estavel = !doHardware || (
      amostrasRef.current.length >= janela &&
      Math.max(...amostrasRef.current) - Math.min(...amostrasRef.current) <= tol
    );
    const kgArred = Math.round(kg * 10) / 10;
    pesoRef.current = kgArred;
    pesoEstavelRef.current = estavel;
    setPeso(kgArred);
    setPesoEstavel(estavel);
    const t = tagAtivaRef.current;
    avaliar(t?.cod ?? null, t?.nova ?? false, kgArred, estavel);
  }

  function limparBrete() {
    registrandoRef.current = false;
    tagAtivaRef.current = null;
    pesoRef.current = null;
    pesoEstavelRef.current = false;
    amostrasRef.current = [];
    setTagAtiva(null);
    setTagInputVal('');
    setPeso(null);
    setPesoEstavel(false);
  }

  // ── Registro de linha ─────────────────────────────────────────────────────

  async function registrar(cod, nova, kg) {
    if (registrandoRef.current) return;
    registrandoRef.current = true;

    const sessaoAtual = sessaoRef.current;
    if (!sessaoAtual) { registrandoRef.current = false; bip('erro'); return; }

    const hora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const agora = new Date().toISOString();
    const animalId = animaisMapRef.current.get(cod) || null;

    // Limpa brete imediatamente (user experience: pronto para o próximo animal)
    limparBrete();
    bip('ok');
    setFeedback({ tipo: 'pronto', msg: `Registrado · ${nf(kg)} kg`, sub: cod + (nova ? ' · entrou como pendência de cadastro' : '') });
    setLinhas(prev => [{ tag: cod, peso: kg, nova, hora }, ...prev]);
    setTimeout(() => setFeedback({ tipo: 'aguardando', msg: 'Aguardando animal', sub: 'Passe o bastão e ponha o animal na balança.' }), 1600);

    const payload = { sessao: sessaoAtual.id, codigo_eletronico: cod, animal: animalId, peso: kg, lido_em: agora };
    try {
      await api.post('/pecuaria/linha-sessao-pesagem/', payload);
    } catch (_) {
      bufferOfflineRef.current.push(payload);
    }
  }

  async function desfazer() {
    setLinhas(prev => {
      if (!prev.length) { bip('erro'); return prev; }
      bip('undo');
      const [ultima, ...resto] = prev;
      setFeedback({ tipo: 'aguardando', msg: 'Removido da sessão', sub: `${ultima.tag} · ${nf(ultima.peso)} kg` });
      setTimeout(() => setFeedback({ tipo: 'aguardando', msg: 'Aguardando animal', sub: 'Passe o bastão e ponha o animal na balança.' }), 1800);
      return resto;
    });
  }

  // ── Flush de buffer offline ───────────────────────────────────────────────

  const flushBuffer = useCallback(async () => {
    if (!sessaoRef.current || !bufferOfflineRef.current.length) return;
    const pendentes = [...bufferOfflineRef.current];
    bufferOfflineRef.current = [];
    for (const payload of pendentes) {
      try {
        await api.post('/pecuaria/linha-sessao-pesagem/', payload);
      } catch (_) {
        bufferOfflineRef.current.push(payload);
      }
    }
  }, []);

  // ── Fechar sessão ─────────────────────────────────────────────────────────

  async function fecharSessao() {
    if (!sessaoRef.current || fechando) return;
    await flushBuffer();
    setFechando(true);
    try {
      const r = await api.post(`/pecuaria/sessao-pesagem/${sessaoRef.current.id}/commit/`);
      const { pesagens_criadas, linhas_pendentes } = r.data;
      window.alert(`Sessão fechada!\n${pesagens_criadas} pesagem(ns) criada(s).${linhas_pendentes ? `\n${linhas_pendentes} pendência(s) sem animal cadastrado.` : ''}`);
      setSessao(null);
      sessaoRef.current = null;
      setLinhas([]);
      limparBrete();
      setFeedback({ tipo: 'aguardando', msg: 'Sessão encerrada', sub: 'Clique em "Iniciar nova sessão" para continuar.' });
    } catch (_) {
      window.alert('Não foi possível fechar a sessão. Tente quando houver conexão.');
    } finally {
      setFechando(false);
    }
  }

  async function iniciarNovaSessao() {
    try {
      const r = await api.post('/pecuaria/sessao-pesagem/', { tipo_evento: 'pesagem', aberta_em: new Date().toISOString() });
      setSessao(r.data);
      sessaoRef.current = r.data;
      setFeedback({ tipo: 'aguardando', msg: 'Aguardando animal', sub: 'Passe o bastão no brinco e ponha o animal na balança.' });
    } catch (_) {
      setErroSessao('Não foi possível criar a sessão. Verifique a conexão.');
    }
  }

  // ── Balança USB (Web Serial) ──────────────────────────────────────────────

  async function conectarSerial() {
    if (!('serial' in navigator)) {
      setFeedback({ tipo: 'erro', msg: 'Navegador sem suporte a USB', sub: 'Use Chrome ou Edge no computador. Enquanto isso, digite o peso.' });
      bip('erro');
      return;
    }
    try {
      const porta = await navigator.serial.requestPort();
      await porta.open({ baudRate: 9600 });
      setSerial(true);
      const dec = new TextDecoderStream();
      porta.readable.pipeTo(dec.writable);
      const leitor = dec.readable.getReader();
      let parcial = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await leitor.read();
        if (done) break;
        parcial += value;
        let i;
        while ((i = parcial.search(/[\r\n]/)) >= 0) {
          const linha = parcial.slice(0, i);
          parcial = parcial.slice(i + 1);
          const m = String(linha).match(/(-?\d+[.,]?\d*)\s*(kg|KG)?/);
          if (m) {
            const kg = parseFloat(m[1].replace(',', '.'));
            if (isFinite(kg) && kg > 0) receberPeso(kg, true);
          }
        }
      }
    } catch (_) {
      setSerial(false);
      setFeedback({ tipo: 'erro', msg: 'Não foi possível abrir a porta', sub: 'Confira o cabo e o driver USB-serial.' });
      bip('erro');
    }
  }

  // ── Derivados para renderização ───────────────────────────────────────────

  const bufLen = bufferOfflineRef.current.length;
  const nTotal = linhas.length;
  const nPend = linhas.filter(l => l.nova).length;
  const media = nTotal ? linhas.reduce((a, l) => a + l.peso, 0) / nTotal : null;
  const faixaInfo = {
    aguardando: { borda: P.borda, bg: P.painel, icone: <MdHourglassEmpty />, cor: P.textoMuted },
    parcial:    { borda: P.info, bg: '#12213F', icone: <MdInfo />, cor: P.info },
    pronto:     { borda: P.sucesso, bg: '#12321B', icone: <MdCheckCircle />, cor: P.sucesso },
    erro:       { borda: P.perigo, bg: '#3A1712', icone: <MdError />, cor: P.perigo },
  }[feedback.tipo] || { borda: P.borda, bg: P.painel, icone: <MdHourglassEmpty />, cor: P.textoMuted };

  // ── Sem permissão ─────────────────────────────────────────────────────────

  if (!podeGravar('pecuaria.sessao_pesagem')) {
    return (
      <Layout>
        <div style={{ background: P.bg, minHeight: '100vh', padding: '32px', color: '#F3B4A4', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
          Sem permissão de gravação em Sessão de Pesagem.
        </div>
      </Layout>
    );
  }

  // ── Erro ao criar sessão ──────────────────────────────────────────────────

  if (erroSessao) {
    return (
      <Layout>
        <div style={{ background: P.bg, minHeight: '100vh', padding: '32px', color: P.texto, fontFamily: 'system-ui,-apple-system,sans-serif' }}>
          <p style={{ color: '#F3B4A4', marginBottom: '16px' }}>{erroSessao}</p>
          <BtnPrimario onClick={() => { setErroSessao(null); iniciarNovaSessao(); }}>Tentar novamente</BtnPrimario>
        </div>
      </Layout>
    );
  }

  // ── Render principal ──────────────────────────────────────────────────────

  return (
    <Layout>
      <EstilosBrete />
      <div style={{ background: P.bg, minHeight: '100vh', padding: '18px', color: P.texto, fontFamily: 'system-ui,-apple-system,sans-serif' }}>

        {/* Topo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', paddingBottom: '14px', borderBottom: `1px solid ${P.borda}`, marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '34px', height: '34px', borderRadius: '9px',
              background: P.destaqueSuave, color: P.destaque, fontSize: '1.15rem',
            }}>
              <TbScale />
            </span>
            <div style={{ fontWeight: 700, letterSpacing: '.01em', fontSize: '1.08rem' }}>
              Pesagem no Brete
              <span style={{ display: 'block', fontWeight: 500, fontSize: '.74rem', color: P.textoMuted, letterSpacing: '.02em' }}>Novalis Agro · Pecuária</span>
            </div>
            <AjudaTela {...AJUDA} />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: 'auto' }}>
            <Chip on={serial} label={serial ? 'Balança USB' : 'Balança manual'} />
            <Chip on={!!sessao} label={sessao ? 'Sessão aberta' : 'Sem sessão'} />
            <Chip on={online} off={!online} label={online ? 'Online' : 'Offline'} />
            {bufLen > 0 && <Chip alerta label={`Buffer ${bufLen}`} />}
          </div>
        </div>

        {/* Faixa de estado — operador lê de longe */}
        <div role="status" aria-live="polite"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: '12px',
            padding: '16px 20px', fontSize: '1.4rem', fontWeight: 700, borderRadius: '12px', marginBottom: '18px',
            border: `2px solid ${faixaInfo.borda}`, background: faixaInfo.bg,
            transition: 'background .15s, border-color .15s',
          }}>
          <span className={feedback.tipo === 'aguardando' ? 'bp-dot-viva' : undefined} style={{ color: faixaInfo.cor, fontSize: '1.5rem', display: 'flex', marginTop: '2px', flexShrink: 0 }}>{faixaInfo.icone}</span>
          <div>
            {feedback.msg}
            {feedback.sub && <small style={{ display: 'block', fontSize: '.82rem', fontWeight: 500, color: P.textoMuted, marginTop: '3px' }}>{feedback.sub}</small>}
          </div>
        </div>

        {/* Grade principal: esquerda (balança + brinco) | direita (sessão + ajustes) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 380px', gap: '18px', alignItems: 'start' }}>

          {/* ── Coluna esquerda ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Peso */}
            <Painel titulo="Peso" icone={<MdOutlineScale />}>
              <div style={{
                margin: '18px 20px 0', padding: '18px 20px', borderRadius: '10px',
                background: P.painelAlt, border: `1px solid ${P.bordaSuave}`,
                display: 'flex', alignItems: 'flex-end', gap: '18px', flexWrap: 'wrap',
              }}>
                <div style={{
                  fontFamily: 'ui-monospace,monospace', fontSize: 'clamp(3.2rem,9vw,5.5rem)', fontWeight: 700,
                  lineHeight: .95, fontVariantNumeric: 'tabular-nums',
                  color: peso != null && !pesoEstavel ? P.destaque : P.texto,
                  textShadow: peso != null && pesoEstavel ? `0 0 22px ${P.sucessoSuave}` : 'none',
                }}>
                  {peso != null ? nf(peso) : '0,0'}
                </div>
                <div style={{ fontFamily: 'ui-monospace,monospace', fontSize: '1.4rem', color: P.destaque, paddingBottom: '.5rem' }}>kg</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', paddingBottom: '.7rem', marginLeft: 'auto', color: P.textoMuted }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block', background: peso == null ? P.neutro : pesoEstavel ? P.sucesso : P.destaque, boxShadow: pesoEstavel && peso != null ? `0 0 6px ${P.sucesso}` : 'none' }} />
                  <span style={{ color: peso == null ? P.textoMuted : P.texto }}>{peso == null ? 'Sem leitura' : pesoEstavel ? 'Estável' : 'Instável'}</span>
                </div>
              </div>
              <div style={{ padding: '16px 20px 0' }}>
                <label style={{ display: 'block', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: P.destaque, fontWeight: 700, marginBottom: '7px' }}>
                  Peso digitado (quando não há balança conectada)
                </label>
                <input type="text" inputMode="decimal" placeholder="ex.: 412,5" autoComplete="off" className="bp-input"
                  style={{ width: '100%', padding: '14px 16px', fontFamily: 'ui-monospace,monospace', fontSize: '1.5rem', letterSpacing: '.06em', background: P.painelAlt, color: P.texto, border: `2px solid ${P.borda}`, borderRadius: '8px' }}
                  onChange={e => {
                    const kg = parseFloat(String(e.target.value).replace(',', '.'));
                    if (isFinite(kg) && kg > 0) receberPeso(kg, false);
                    else { setPeso(null); setPesoEstavel(false); amostrasRef.current = []; }
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '9px', padding: '16px 20px 20px', flexWrap: 'wrap' }}>
                <BtnPrimario icone={<MdUsb />} onClick={conectarSerial} disabled={serial}>Conectar balança (USB)</BtnPrimario>
                <Btn onClick={() => {
                  clearInterval(simulandoRef.current);
                  const alvo = 280 + (Date.now() % 260); // pseudo-random sem Math.random
                  let n = 0;
                  amostrasRef.current = [];
                  simulandoRef.current = setInterval(() => {
                    n++;
                    const r = n < 9 ? ((n * 7919 % 100) / 100 - .5) * (26 - n * 2.6) : ((n * 3541 % 10) / 10 - .5) * 1.1;
                    receberPeso(Math.round((alvo + r) * 10) / 10, true);
                    if (n > 22) clearInterval(simulandoRef.current);
                  }, 190);
                }}>Simular balança</Btn>
                <Btn onClick={() => { clearInterval(simulandoRef.current); setPeso(null); setPesoEstavel(false); amostrasRef.current = []; }}>Tirar animal</Btn>
              </div>
            </Painel>

            {/* Identificação eletrônica */}
            <Painel titulo="Identificação eletrônica" icone={<MdNfc />}>
              <div style={{ padding: '18px 20px 0' }}>
                <label style={{ display: 'block', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: P.destaque, fontWeight: 700, marginBottom: '7px' }}>
                  Brinco eletrônico — 15 dígitos (ISO 11784/11785)
                </label>
                <input type="text" inputMode="numeric" autoComplete="off" value={tagInputVal} className="bp-input"
                  placeholder="aguardando bastão…"
                  style={{ width: '100%', padding: '14px 16px', fontFamily: 'ui-monospace,monospace', fontSize: '1.5rem', letterSpacing: '.06em', background: P.painelAlt, color: P.texto, border: `2px solid ${tagAtiva ? P.sucesso : P.borda}`, borderRadius: '8px' }}
                  onChange={e => setTagInputVal(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const cod = normalizar(tagInputVal);
                      if (cod.length === 15) receberTag(cod);
                      else { bip('erro'); setFeedback({ tipo: 'erro', msg: 'Brinco precisa ter 15 dígitos', sub: `Recebido: ${cod.length} dígito(s).` }); }
                    }
                  }}
                />
                <p style={{ fontSize: '.8rem', color: P.textoMuted, marginTop: '9px', lineHeight: 1.45 }}>
                  Mantenha a página em foco e passe o bastão. Também funciona se o foco escapar — a leitura é capturada na página inteira. Digitação manual aceita com <kbd style={{ fontFamily: 'ui-monospace,monospace', fontSize: '.78rem', background: P.painelAlt, border: `1px solid ${P.borda}`, borderRadius: '4px', padding: '1px 6px' }}>Enter</kbd>.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '9px', padding: '14px 20px 20px', flexWrap: 'wrap' }}>
                <Btn icone={<MdAdd />} onClick={() => receberTag('076000123456789')}>Simular brinco cadastrado</Btn>
                <Btn icone={<MdAdd />} onClick={() => receberTag('076000' + String((Date.now() % 1e9)).padStart(9, '0'))}>Simular brinco novo</Btn>
                <BtnPerigo icone={<MdUndo />} onClick={desfazer} disabled={!linhas.length}>
                  Desfazer último{' '}
                  <kbd style={{ fontFamily: 'ui-monospace,monospace', fontSize: '.78rem', background: P.painelAlt, border: `1px solid ${P.borda}`, borderRadius: '4px', padding: '1px 6px' }}>F2</kbd>
                </BtnPerigo>
              </div>
            </Painel>
          </div>

          {/* ── Coluna direita ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Sessão */}
            <Painel titulo="Sessão" icone={<MdList />}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1px', background: P.borda }}>
                <Contador valor={nTotal} label="Pesados" />
                <Contador valor={nPend} label="Novos" aviso={nPend > 0} />
                <Contador valor={media != null ? nf(media) : '—'} label="Média kg" />
              </div>
              <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.84rem' }}>
                  <thead>
                    <tr>
                      {['Brinco', 'Peso', 'Hora'].map((h, i) => (
                        <th key={h} style={{ textAlign: i === 1 ? 'right' : 'left', padding: '9px 12px', fontSize: '.66rem', textTransform: 'uppercase', letterSpacing: '.07em', color: P.destaque, borderBottom: `1px solid ${P.borda}`, position: 'sticky', top: 0, background: P.painel }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.length === 0 ? (
                      <tr><td colSpan={3} style={{ padding: '30px 14px', textAlign: 'center', color: P.textoMuted, fontSize: '.86rem' }}>Nenhum animal pesado ainda.</td></tr>
                    ) : linhas.map((l, i) => (
                      <tr key={i} className="bp-row" style={{ background: l.nova ? P.destaqueSuave : undefined, borderBottom: `1px solid ${P.bordaSuave}` }}>
                        <td style={{ padding: '10px 12px', fontFamily: 'ui-monospace,monospace', fontSize: '.82rem' }}>
                          {l.tag}
                          {l.nova && <span style={{ color: P.destaque, fontFamily: 'system-ui', fontSize: '.68rem', fontWeight: 700 }}> novo</span>}
                        </td>
                        <td style={{ padding: '10px 12px', fontFamily: 'ui-monospace,monospace', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{nf(l.peso)}</td>
                        <td style={{ padding: '10px 12px', color: P.textoMuted }}>{l.hora}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', gap: '9px', padding: '16px', flexWrap: 'wrap', borderTop: `1px solid ${P.borda}` }}>
                <Btn icone={<MdDownload />} onClick={() => {
                  if (!linhas.length) return;
                  const rows = [['brinco', 'peso_kg', 'hora', 'cadastrado'], ...linhas.map(l => [l.tag, nf(l.peso), l.hora, l.nova ? 'nao' : 'sim'])];
                  const csv = rows.map(r => r.join(';')).join('\n');
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
                  a.download = 'pesagem-brete.csv';
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}>Baixar CSV</Btn>
                {!sessao
                  ? <BtnPrimario icone={<MdPlayArrow />} onClick={iniciarNovaSessao}>Iniciar nova sessão</BtnPrimario>
                  : <BtnPerigo icone={<MdStopCircle />} onClick={fecharSessao} disabled={fechando}>{fechando ? 'Fechando…' : 'Fechar sessão'}</BtnPerigo>
                }
              </div>
            </Painel>

            {/* Ajustes */}
            <Painel titulo="Ajustes da sessão" icone={<MdTune />}>
              <div style={{ padding: '16px', display: 'grid', gap: '13px' }}>
                <Ajuste label="Tolerância de estabilidade (kg)" value={ajusteTol} onChange={setAjusteTol} />
                <Ajuste label="Leituras estáveis exigidas" value={ajusteJan} onChange={setAjusteJan} />
                <Ajuste label="Brinco expira em (s)" value={ajusteExp} onChange={setAjusteExp} />
              </div>
              <p style={{ fontSize: '.78rem', lineHeight: 1.5, color: P.textoMuted, padding: '13px 16px', borderTop: `1px solid ${P.borda}` }}>
                Cada linha é gravada no servidor no momento da leitura. Se offline, fica em buffer local e é enviada quando a rede voltar. Ao fechar a sessão, as pesagens são registradas no histórico do animal.
              </p>
            </Painel>
          </div>
        </div>
      </div>
    </Layout>
  );
}
